import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useBreadcrumb } from "shared/context/BreadcrumbContext";
import AnalyticsTopBar from "features/analytics/studio/components/shell/AnalyticsTopBar";
import QueryBuilderPanel from "features/analytics/studio/components/query/QueryBuilderPanel";
import InsightsEmptyState from "features/analytics/studio/components/shell/InsightsEmptyState";
import ReportLayout from "features/analytics/studio/components/report/ReportLayout";
import InsightsChart from "features/analytics/studio/components/charts/InsightsChart";
import InsightsDataTable from "features/analytics/studio/components/charts/InsightsDataTable";
import MetricCards from "features/analytics/studio/components/charts/MetricCards";
import FunnelVisualization from "features/analytics/studio/components/charts/FunnelVisualization";
import FunnelLineChart from "features/analytics/studio/components/charts/FunnelLineChart";
import FunnelSummaryBarChart from "features/analytics/studio/components/charts/FunnelSummaryBarChart";
import FunnelBreakdownBarChart from "features/analytics/studio/components/charts/FunnelBreakdownBarChart";
import FunnelMetricCards from "features/analytics/studio/components/charts/FunnelMetricCards";
import FunnelDataTable from "features/analytics/studio/components/charts/FunnelDataTable";
import FunnelTimeSeriesTable from "features/analytics/studio/components/charts/FunnelTimeSeriesTable";
import FunnelStepUsersPanel from "features/analytics/studio/components/funnel/FunnelStepUsersPanel";
import RetentionVisualization from "features/analytics/studio/components/charts/RetentionVisualization";
import RetentionMetricTable from "features/analytics/studio/components/charts/RetentionMetricTable";
import {
  sliceBreakdownsForChart,
  RETENTION_BREAKDOWN_CHART_TOP_DEFAULT,
} from "features/analytics/studio/components/charts/BreakdownTopValuesControl";
import EventsTimeFilter from "features/activity/components/EventsTimeFilter";
import { useAnalytics } from "features/analytics/studio/context/AnalyticsContext";
import { useInsights, useEventsMetadata, useCards, useHomeWorkspaceCanvas } from "features/analytics/studio/hooks/useInsights";
import { useBoards } from "features/analytics/studio/hooks/useBoards";
import SaveToBoardModal from "features/analytics/studio/components/boards/SaveToBoardModal";
import ChartNameModal from "features/analytics/studio/components/boards/ChartNameModal";
import { isBlankOrDefaultChartTitle } from "features/analytics/studio/utils/chartTitle";
import { useAppDialog } from "shared/context/AppDialogContext";
import * as analyticsApi from "features/analytics/studio/api/analyticsApi";
import { upsertCardInHomeWorkspaceCache } from "shared/utils/homeWorkspaceCache";
import { trackClientEvent } from "shared/utils/trodoEvents";
import { formatNumber } from "shared/utils/numberFormatter";
import { isInsightTimeSeriesChart, INSIGHT_CHART_TYPES } from "features/analytics/studio/constants/chartOptions";
import { DEFAULT_FUNNEL_TABLE_COLUMNS } from "features/analytics/studio/constants/funnelTableColumns";
import {
  funnelChartBucketRange,
  formatFunnelBucketPeriodLabel,
  segmentLabelToBreakdownValues,
} from "features/analytics/studio/utils/funnelChartBucketRange";

/** Line, stacked-line, column/bar, stacked variants, pie — excludes table + metric card. */
const INSIGHT_DATAPOINT_DRILL_CHART_IDS = new Set(
  INSIGHT_CHART_TYPES.filter((x) => x.id !== "table" && x.id !== "metric").map((x) => x.id)
);

/** Default chart config for funnels so axis options apply before panel syncs (matches QueryBuilderPanel defaults). */
const DEFAULT_FUNNEL_CHART_CONFIG = {
  axis: {
    leftY: { visible: true, label: "", unit: "#", customUnit: "", abbreviation: true, decimals: ".0", thresholdMin: "", thresholdMax: "", outOfBounds: "hidden", scale: "linear" },
    rightY: { visible: true, label: "", unit: "#", customUnit: "", abbreviation: true, decimals: ".0", thresholdMin: "", thresholdMax: "", outOfBounds: "hidden", scale: "linear" },
    xAxis: { visible: true, label: "" },
  },
  themeId: "trodo-default",
  /** First series color defaults to brand primary (see FunnelSummaryBarChart, FunnelLineChart). */
  colors: ["var(--brand-primary)"],
  funnelTableColumns: { ...DEFAULT_FUNNEL_TABLE_COLUMNS },
};

/** Map analytics preset (e.g. "30D") to EventsTimeFilter preset (e.g. "30d") */
function analyticsPresetToEventsPreset(analyticsPreset) {
  if (!analyticsPreset) return "30d";
  const p = String(analyticsPreset);
  if (p === "Today") return "today";
  if (p === "Yesterday") return "yesterday";
  if (p === "7D") return "7d";
  if (p === "30D") return "30d";
  if (p === "3M") return "3m";
  if (p === "6M") return "6m";
  if (p === "12M") return "12m";
  return "30d";
}

/** Preview height aligned with editor panel — line, stacked line, bar, column share one band; table/pie slightly shorter. */
function insightEditorChartHeightPx(chartType) {
  if (chartType === "table") return 300;
  if (chartType === "pie") return 340;
  return 380;
}

/**
 * Normalize custom date range to UTC calendar-day boundaries so the chart shows
 * data from the selected start date only (no previous day). EventsTimeFilter
 * sends local midnight (e.g. Jan 14 00:00 IST = 2026-01-13T18:30:00.000Z); we must
 * use the calendar date the user selected (local), not the UTC date from the ISO.
 * - start: selected start calendar date at 00:00:00.000Z
 * - end: day after selected end at 00:00:00.000Z (backend uses timestamp < end)
 */
function normalizeCustomRangeToUtcDays(fromIso, toIso, enableTimeRanges) {
  if (enableTimeRanges) {
    return { start: fromIso, end: toIso };
  }
  const toLocalYmd = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const fromYmd = toLocalYmd(fromIso);
  const toYmd = toLocalYmd(toIso);
  if (!fromYmd || !toYmd) return { start: fromIso, end: toIso };
  const nextDayYmd = (ymd) => {
    const d = new Date(ymd + "T12:00:00.000Z");
    d.setUTCDate(d.getUTCDate() + 1);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return {
    start: fromYmd + "T00:00:00.000Z",
    end: nextDayYmd(toYmd) + "T00:00:00.000Z",
  };
}

// Helper function to sort and limit funnel breakdowns (similar to insights)
function sortAndLimitFunnelBreakdowns(breakdowns, breakdownLimit, breakdownSortOrder, breakdownShowMax) {
  if (!breakdowns || !Array.isArray(breakdowns) || breakdowns.length === 0) {
    return [];
  }
  
  const maxLimit = breakdownShowMax ? 50 : Math.min(breakdownLimit, 50);
  
  // Calculate average for each breakdown (using step1 count as the metric)
  const breakdownsWithAverage = breakdowns.map(breakdown => {
    const step1Data = breakdown.steps?.step1 || {};
    const step1Count = step1Data.count || 0;
    return {
      ...breakdown,
      calculatedAverage: step1Count,
      breakdownKey: breakdown.breakdownValue || breakdown.breakdownKey || 'Unknown'
    };
  });
  
  // Sort breakdowns
  const sortFn = (() => {
    switch (breakdownSortOrder) {
      case 'segment-a-z':
        return (a, b) => (a.breakdownKey || '').localeCompare(b.breakdownKey || '');
      case 'segment-z-a':
        return (a, b) => (b.breakdownKey || '').localeCompare(a.breakdownKey || '');
      case 'average-asc':
        return (a, b) => a.calculatedAverage - b.calculatedAverage;
      case 'average-desc':
      default:
        return (a, b) => b.calculatedAverage - a.calculatedAverage;
    }
  })();
  
  breakdownsWithAverage.sort(sortFn);
  
  // Limit to maxLimit
  return breakdownsWithAverage.slice(0, maxLimit);
}

// Build funnel line-chart data: conversion % or time-to-convert (seconds) per bucket + users + delta
function buildFunnelLineData(funnelData, measuring = "conversion_rate") {
  const steps = funnelData?.steps || [];
  const timeSeries = funnelData?.timeSeries || [];

  if (!steps.length || steps.length < 2 || !timeSeries.length) {
    return {
      points: [],
      title:
        measuring === "time_to_convert"
          ? "Time to convert"
          : "Funnel Conversion Rate",
    };
  }

  const firstKey = "step1";
  const lastKey = `step${steps.length}`;
  const firstLabel = steps[0]?.label || steps[0]?.eventName || "Step 1";
  const lastLabel =
    steps[steps.length - 1]?.label ||
    steps[steps.length - 1]?.eventName ||
    `Step ${steps.length}`;

  const points = [];
  let prevMetric = null;

  timeSeries.forEach((row) => {
    const values = row.values || {};
    const first = values[firstKey] != null ? values[firstKey] : 0;
    const last = values[lastKey] != null ? values[lastKey] : 0;

    const lastValueKey = `step${steps.length}_value`;
    const lastMetric =
      values[lastValueKey] != null && values[lastValueKey] !== ""
        ? parseFloat(values[lastValueKey])
        : null;

    let conversion;
    if (measuring === "time_to_convert") {
      const sec = values.funnel_ttc_seconds;
      conversion =
        sec != null && sec !== "" ? parseFloat(sec) : 0;
      if (Number.isNaN(conversion)) conversion = 0;
    } else if (
      measuring === "total_conversions" ||
      measuring === "total_sessions" ||
      measuring === "unique_users" ||
      measuring === "property_sum"
    ) {
      conversion =
        lastMetric != null && Number.isFinite(lastMetric)
          ? lastMetric
          : 0;
    } else {
      conversion = first > 0 ? (last / first) * 100 : 0;
    }

    const delta =
      prevMetric != null ? conversion - prevMetric : null;

    points.push({
      date: row.date,
      conversion,
      users: last,
      delta,
    });

    prevMetric = conversion;
  });

  const title =
    measuring === "time_to_convert"
      ? `Time to convert (${firstLabel} → ${lastLabel})`
      : measuring === "total_conversions"
      ? `Total conversions (${firstLabel} → ${lastLabel})`
      : measuring === "total_sessions"
      ? `Sessions (${firstLabel} → ${lastLabel})`
      : measuring === "unique_users"
      ? `Unique users (${firstLabel} → ${lastLabel})`
      : measuring === "property_sum"
      ? `Property sum (${firstLabel} → ${lastLabel})`
      : `Conversion Rate of ${firstLabel} through ${lastLabel} \u2013 All Steps`;

  return { points, title };
}

// Maps EventsTimeFilter preset keys to analytics preset names
const EVENTS_PRESET_TO_ANALYTICS = {
  today: "Today",
  yesterday: "Yesterday",
  "7d": "7D",
  "30d": "30D",
  "3m": "3M",
  "6m": "6M",
  "12m": "12M",
};

const INSIGHT_SERIES_INTERVALS = ["hour", "day", "week", "month"];

/** Build timeRange for buildInsightsQuery from insight time filter or global preset */
function getInsightTimeRange(insightTimeFilter, globalTimeRange, seriesInterval = "day") {
  const interval = INSIGHT_SERIES_INTERVALS.includes(seriesInterval) ? seriesInterval : "day";
  if (insightTimeFilter && insightTimeFilter.type === "preset" && insightTimeFilter.preset) {
    // Use the named preset so the backend can cache by preset key and apply relative date logic
    const preset = EVENTS_PRESET_TO_ANALYTICS[insightTimeFilter.preset] || globalTimeRange || "30D";
    return {
      preset,
      start: null,
      end: null,
      interval,
      timezone: "UTC",
    };
  }
  if (insightTimeFilter && insightTimeFilter.from && insightTimeFilter.to) {
    // Custom date range
    const { start, end } = normalizeCustomRangeToUtcDays(
      insightTimeFilter.from,
      insightTimeFilter.to,
      insightTimeFilter.enableTimeRanges === true
    );
    return {
      preset: null,
      start,
      end,
      interval,
      timezone: "UTC",
    };
  }
  return {
    preset: globalTimeRange || "30D",
    start: null,
    end: null,
    interval,
    timezone: "UTC",
  };
}

// Simple editor for Text or Media cards (edit content/url in place)
function TextOrMediaCardEditor({ team, boardId, teamId, isHomeEditor: homeEd, cardId, editorType, card, cards, updateCard, boardName = "" }) {
  const navigate = useNavigate();
  const { alert: appAlert } = useAppDialog();
  const [title, setTitle] = useState(card?.title || "Untitled");
  const [content, setContent] = useState(
    editorType === "text" ? (card?.queryDsl?.content ?? "") : ""
  );
  const [url, setUrl] = useState(editorType === "media" ? (card?.queryDsl?.url ?? "") : "");
  const [mediaType, setMediaType] = useState(editorType === "media" ? (card?.queryDsl?.type || "image") : "image");
  const [saving, setSaving] = useState(false);
  useBreadcrumb(
    homeEd
      ? [{ label: "Home", to: `/${team}/home` }]
      : [
          { label: "Reports", to: `/${team}/analytics/reports` },
          { label: boardName || "Board", to: `/${team}/analytics/boards/${boardId}` },
        ]
  );

  useEffect(() => {
    if (card) {
      setTitle(card.title || "Untitled");
      if (editorType === "text") setContent(card.queryDsl?.content ?? "");
      if (editorType === "media") {
        setUrl(card.queryDsl?.url ?? "");
        setMediaType(card.queryDsl?.type || "image");
      }
    }
  }, [card, editorType]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates =
        editorType === "text"
          ? { title: title.trim() || "Untitled", queryDsl: { content: content } }
          : { title: title.trim() || "Untitled", queryDsl: { url: url.trim(), type: mediaType } };
      const updated = await updateCard(cardId, updates);
      if (homeEd && teamId != null && updated) upsertCardInHomeWorkspaceCache(teamId, updated);
      navigate(homeEd ? `/${team}/home` : `/${team}/analytics/boards/${boardId}`);
    } catch (err) {
      console.error("Failed to update card:", err);
      await appAlert("Failed to save. Please try again.", { title: "Save failed", variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate(homeEd ? `/${team}/home` : `/${team}/analytics/boards/${boardId}`);
  };

  if (!card && cards.length > 0) {
    return (
      <div className="flex flex-1 items-center justify-center text-[13px] text-[color:var(--text-muted)]" style={{ fontFamily: "Inter, sans-serif" }}>
        Card not found
      </div>
    );
  }

  if (!card) {
    return (
      <div className="flex flex-1 items-center justify-center text-[13px] text-[color:var(--text-muted)]" style={{ fontFamily: "Inter, sans-serif" }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-surface" style={{ fontFamily: "Inter, sans-serif" }}>
      <AnalyticsTopBar
        showCreateNew={false}
        showTimeRange={false}
        showSaveClose={true}
        onSave={handleSave}
        onClose={handleClose}
      />
      <main className="flex-1 overflow-auto p-6">
        <div className="max-w-2xl space-y-4">
          <div>
            <label className="block text-[12px] font-medium mb-1.5 text-[color:var(--text-secondary)]">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full h-8 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[13px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--brand-primary)] transition-colors"
            />
          </div>
          {editorType === "text" && (
            <div>
              <label className="block text-[12px] font-medium mb-1.5 text-[color:var(--text-secondary)]">Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={12}
                className="w-full rounded-md border border-[color:var(--border-default)] bg-surface px-3 py-2 text-[13px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--brand-primary)] transition-colors resize-y"
                placeholder="Use text to tell a story..."
              />
            </div>
          )}
          {editorType === "media" && (
            <>
              <div>
                <label className="block text-[12px] font-medium mb-1.5 text-[color:var(--text-secondary)]">URL</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full h-8 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[13px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--brand-primary)] transition-colors"
                  placeholder="Image or video URL"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium mb-1.5 text-[color:var(--text-secondary)]">Type</label>
                <select
                  value={mediaType}
                  onChange={(e) => setMediaType(e.target.value)}
                  className="h-8 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[13px] text-[color:var(--text-primary)] outline-none"
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default function InsightsEditorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { team, boardId } = useParams();
  const isHomeEditor =
    typeof location.pathname === "string" &&
    (location.pathname.includes("/home/editor") || location.pathname.includes("/analytics/home/editor"));
  const [searchParams] = useSearchParams();
  const editorType = searchParams.get("type") || "insights";
  const reportType = editorType === "funnels" ? "funnel" : editorType === "retention" ? "retention" : "insight";
  const cardIdParam = searchParams.get("cardId");
  const gridX = searchParams.get("x") != null ? parseInt(searchParams.get("x"), 10) : null;
  const gridY = searchParams.get("y") != null ? parseInt(searchParams.get("y"), 10) : null;
  const gridW = searchParams.get("w") != null ? parseInt(searchParams.get("w"), 10) : null;
  const gridH_param = searchParams.get("h") != null ? parseInt(searchParams.get("h"), 10) : null;
  const selectedTeam = JSON.parse(localStorage.getItem("selectedTeamData") || "null");
  const teamId = selectedTeam?._id ?? selectedTeam?.id ?? null;
  const { globalTimeRange, setGlobalTimeRange, TIME_RANGES } = useAnalytics();
  const { result, loading, error, execute, beginNewQuery, resetLoading } = useInsights();
  const { events, loadEvents } = useEventsMetadata();
  const { createBoard, loadBoards } = useBoards();
  const homeCanvas = useHomeWorkspaceCanvas(teamId, { mode: "default" }, isHomeEditor);
  const boardCards = useCards(isHomeEditor ? null : boardId, teamId);
  const cards = isHomeEditor ? homeCanvas.cards : boardCards.cards;
  const loadCards = isHomeEditor ? homeCanvas.loadCards : boardCards.loadCards;
  const updateCard = isHomeEditor ? homeCanvas.updateCard : boardCards.updateCard;
  const editingCard = cardIdParam && cards.find((c) => String(c.id) === String(cardIdParam));
  const { alert: appAlert } = useAppDialog();
  const [saveBoardModalOpen, setSaveBoardModalOpen] = useState(false);
  const [chartNameModalOpen, setChartNameModalOpen] = useState(false);
  const [funnelStepUsersOpen, setFunnelStepUsersOpen] = useState(false);
  const [funnelStepUsersContext, setFunnelStepUsersContext] = useState({
    stepIndex: 0,
    breakdownValues: null,
    stepTitle: "",
    timeSlice: null,
    periodLabel: null,
  });
  const [insightDatapointUsersOpen, setInsightDatapointUsersOpen] = useState(false);
  const [insightDatapointContext, setInsightDatapointContext] = useState({
    metricId: null,
    breakdownValues: null,
    stepTitle: "",
    timeSlice: null,
    periodLabel: null,
  });
  const [saveActionLoading, setSaveActionLoading] = useState(false);
  const [editorSaveLoading, setEditorSaveLoading] = useState(false);
  const pendingSaveRef = useRef(null);
  const saveAnchorRef = useRef(null);
  const [boardName, setBoardName] = useState("");
  useBreadcrumb(
    isHomeEditor
      ? [{ label: "Home", to: `/${team}/home` }]
      : [
          { label: "Reports", to: `/${team}/analytics/reports` },
          boardId && boardId !== "new"
            ? { label: boardName || "Board", to: `/${team}/analytics/boards/${boardId}` }
            : { label: boardName || "Board" },
        ]
  );

  /** Same shape as Events page: { type: 'preset'|'custom', preset?, from, to }. When set, from/to are used in queryDSL. */
  const [insightTimeFilter, setInsightTimeFilter] = useState(null);

  const [activeTab, setActiveTab] = useState("query");
  useEffect(() => {
    if (activeTab === "annotations") setActiveTab("query");
  }, [activeTab]);
  // New metrics array structure (insight)
  const [metrics, setMetrics] = useState([]);
  // Funnel steps (funnel report type)
  const [funnelSteps, setFunnelSteps] = useState([]);
  const [funnelMeasuring, setFunnelMeasuring] = useState("conversion_rate");
  const [funnelMeasuringProperty, setFunnelMeasuringProperty] = useState(null);
  const [funnelMeasuringAggregation, setFunnelMeasuringAggregation] = useState("average");
  const [funnelMeasuringPercentileValue, setFunnelMeasuringPercentileValue] = useState(50);
  const [funnelConversionWindow, setFunnelConversionWindow] = useState({ value: 7, unit: 'days' });
  /** When true, funnel counts users who completed all steps in any order within the conversion window. */
  const [funnelAnyOrder, setFunnelAnyOrder] = useState(false);
  /** Funnel grain: same as insight metrics (User vs group key); persisted on definition. */
  const [funnelCountSubject, setFunnelCountSubject] = useState("user");
  const [funnelAccumulationGroupKey, setFunnelAccumulationGroupKey] = useState(null);

  const handleFunnelMeasuringChange = useCallback((next) => {
    setFunnelMeasuring(next);
    if (next === "time_to_convert") {
      setChartType((ct) => (ct === "funnel-steps" ? "bar" : ct));
    }
  }, []);

  useEffect(() => {
    if (reportType !== "funnel") return;
    if (funnelMeasuring !== "time_to_convert") return;
    setChartType((ct) => (ct === "funnel-steps" ? "bar" : ct));
  }, [reportType, funnelMeasuring]);

  // Retention (retention report type)
  const [retentionStartEvent, setRetentionStartEvent] = useState("");
  const [retentionReturnEvent, setRetentionReturnEvent] = useState("");
  const [retentionStartEventSource, setRetentionStartEventSource] = useState("event");
  const [retentionReturnEventSource, setRetentionReturnEventSource] = useState("event");
  const [retentionInterval, setRetentionInterval] = useState("day");
  const [retentionRule, setRetentionRule] = useState("on_or_after");
  const [retentionMetric, setRetentionMetric] = useState("retention_rate");
  /** Per-user vs per-group cohort/return counting (same as funnel grain) */
  const [retentionCountSubject, setRetentionCountSubject] = useState("user");
  const [retentionAccumulationGroupKey, setRetentionAccumulationGroupKey] = useState(null);
  /** Metric view: max breakdown lines on summary cards (table lists all); default 6, max 50 */
  const [retentionMetricBreakdownTopN, setRetentionMetricBreakdownTopN] = useState(
    RETENTION_BREAKDOWN_CHART_TOP_DEFAULT
  );
  // Legacy state (for backward compatibility during migration)
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedAggregation, setSelectedAggregation] = useState("count");
  const [metricFilters, setMetricFilters] = useState([]);
  const [globalFilters, setGlobalFilters] = useState([]);
  const [breakdowns, setBreakdowns] = useState([]); // New: array of breakdowns

  const retentionBreakdownSignature = useMemo(() => {
    const b = breakdowns?.[0];
    if (!b) return "";
    return `${String(b.property ?? "")}\0${String(b.propertySource ?? "")}\0${String(b.step ?? 1)}`;
  }, [breakdowns]);

  useEffect(() => {
    if (reportType !== "retention") return;
    setRetentionMetricBreakdownTopN(RETENTION_BREAKDOWN_CHART_TOP_DEFAULT);
  }, [reportType, retentionBreakdownSignature]);
  // Breakdown sorting and limiting state
  const [breakdownSortOrder, setBreakdownSortOrder] = useState('average-desc'); // 'segment-a-z', 'segment-z-a', 'average-asc', 'average-desc'
  const [breakdownLimit, setBreakdownLimit] = useState(10);
  const [breakdownShowMax, setBreakdownShowMax] = useState(false);
  const [isBreakdownLimitDropdownOpen, setIsBreakdownLimitDropdownOpen] = useState(false);
  const [breakdownCustomLimitInput, setBreakdownCustomLimitInput] = useState('10');
  const breakdownLimitDropdownRef = useRef(null);
  const [chartType, setChartType] = useState(
    reportType === "funnel" ? "funnel-steps" : 
    reportType === "retention" ? "retention-curve" : 
    "line"
  );
  const [cardTitle, setCardTitle] = useState("");
  const [chartConfig, setChartConfig] = useState(null); // axis, themeId, colors from Chart section
  /** Time bucket for insight time-series charts (saved in queryDsl.time.interval). */
  const [insightSeriesInterval, setInsightSeriesInterval] = useState("day");
  const hydratedCardIdRef = useRef(null);
  const flowPrefillSigRef = useRef("");

  /** Open Funnels from Flow editor "View as Funnel" with matching steps, window, filters, time. */
  useEffect(() => {
    const pre = location.state?.flowFunnelPrefill;
    if (!pre || editorType !== "funnels") return;
    const sig = JSON.stringify(pre);
    if (sig === flowPrefillSigRef.current) return;
    flowPrefillSigRef.current = sig;

    if (pre.steps && Array.isArray(pre.steps) && pre.steps.length > 0) {
      setFunnelSteps(
        pre.steps.map((s, i) => ({
          id: s.id || `step-${i + 1}`,
          eventName: String(s.eventName || s.event || "").trim(),
          label: String(s.label || s.eventName || s.event || "").trim(),
          source: s.source || "event",
          filters: Array.isArray(s.filters) ? s.filters : [],
        }))
      );
    }
    if (pre.conversionWindow && typeof pre.conversionWindow === "object") {
      setFunnelConversionWindow({
        value: pre.conversionWindow.value ?? 7,
        unit: pre.conversionWindow.unit || "days",
      });
    }
    if (pre.filters && Array.isArray(pre.filters) && pre.filters.length > 0) {
      setGlobalFilters(
        pre.filters.map((f, i) => ({
          id: f.id || `gf-prefill-${i}`,
          field: f.field ?? f.property,
          property: f.field ?? f.property,
          operator: f.operator,
          value: f.value,
          value2: f.value2,
          propertyType: f.propertyType,
          propertySource: f.propertySource,
          category: f.category || "global",
          connector: i === 0 ? "WHERE" : "AND",
        }))
      );
    }
    const t = pre.time;
    if (t?.start && t?.end) {
      setInsightTimeFilter({ type: "custom", from: t.start, to: t.end });
    } else if (t?.preset) {
      setInsightTimeFilter(null);
      setGlobalTimeRange(t.preset);
    }

    navigate(`${location.pathname}${location.search || ""}`, { replace: true, state: null });
  }, [location.state, editorType, location.pathname, location.search, navigate, setGlobalTimeRange]);

  // Load board name for breadcrumb (Boards / <Board name>; chart name is edited in the top bar)
  useEffect(() => {
    if (isHomeEditor) {
      setBoardName("My workspace");
      return;
    }
    if (!boardId || boardId === "new") {
      setBoardName("");
      return;
    }
    let cancelled = false;
    analyticsApi.fetchBoard(boardId, false, teamId).then((res) => {
      if (cancelled) return;
      const data = res?.data ?? res;
      setBoardName(data?.name || "Board");
    }).catch(() => {
      if (!cancelled) setBoardName("Board");
    });
    return () => { cancelled = true; };
  }, [boardId, teamId, isHomeEditor]);

  // Hydrate state from existing card when opening with cardId (so Edit opens the same card, not a new one)
  useEffect(() => {
    if (!cardIdParam || !editingCard) return;
    if (hydratedCardIdRef.current === cardIdParam) return;
    hydratedCardIdRef.current = cardIdParam;
    const q = editingCard.queryDsl && typeof editingCard.queryDsl === "object" ? editingCard.queryDsl : {};

    if (editorType === "funnels" && q.definition?.steps) {
      const stepName = (s) => (s.event != null ? String(s.event).trim() : (s.eventName != null ? String(s.eventName).trim() : ""));
      setFunnelSteps(
        (q.definition.steps || []).map((s, i) => ({
          id: s.id || `step-${i + 1}`,
          eventName: stepName(s) || "",
          label: (s.label ?? s.event ?? s.eventName ?? "").trim() || "",
          source: s.source || "event",
          filters: Array.isArray(s.filters) ? s.filters : [],
        }))
      );
      setCardTitle(editingCard.title ?? "");
      const vis = editingCard.visualization || q.visualization || {};
      const def = q.definition;
      let nextChartType = vis.chartType || "funnel-steps";
      if (def?.measuring === "time_to_convert" && nextChartType === "funnel-steps") {
        nextChartType = "bar";
      }
      setChartType(nextChartType);
      if (vis.config && typeof vis.config === "object") setChartConfig(vis.config);
      if (def?.measuring) setFunnelMeasuring(def.measuring);
      if (def?.measuringProperty !== undefined) setFunnelMeasuringProperty(def.measuringProperty || null);
      if (def?.measuringAggregation) setFunnelMeasuringAggregation(def.measuringAggregation);
      if (typeof def?.measuringPercentileValue === "number") setFunnelMeasuringPercentileValue(def.measuringPercentileValue);
      if (def?.conversionWindow && typeof def.conversionWindow === "object" && def.conversionWindow.value != null && def.conversionWindow.unit)
        setFunnelConversionWindow({ value: def.conversionWindow.value, unit: def.conversionWindow.unit });
      setFunnelAnyOrder(def?.anyOrder === true);
      if (def?.accumulationGroupKey && String(def.accumulationGroupKey).trim() !== "") {
        setFunnelCountSubject("group");
        setFunnelAccumulationGroupKey(String(def.accumulationGroupKey).trim());
      } else {
        setFunnelCountSubject(def?.countSubject === "group" ? "group" : "user");
        setFunnelAccumulationGroupKey(null);
      }
      const defFilters = def?.filters;
      if (defFilters && Array.isArray(defFilters) && defFilters.length > 0) {
        setGlobalFilters(defFilters.map((f, i) => ({
          id: f.id || `global-filter-${i}-${Date.now()}`,
          field: f.field ?? f.property,
          property: f.field ?? f.property,
          operator: f.operator,
          value: f.value,
          value2: f.value2,
          propertyType: f.propertyType,
          propertySource: f.propertySource,
          category: f.category || "global",
          listMode: f.listMode,
          listItemType: f.listItemType,
          propertyLabel: f.propertyLabel,
          connector: i === 0 ? "WHERE" : "AND",
        })));
      } else {
        setGlobalFilters([]);
      }
      // Load breakdowns (support both new array format and legacy single breakdown)
      const defBreakdowns = def?.breakdowns;
      if (defBreakdowns && Array.isArray(defBreakdowns) && defBreakdowns.length > 0) {
        setBreakdowns(defBreakdowns);
      } else if (q.definition?.breakdown) {
        // Legacy: convert single breakdown to array
        setBreakdowns([q.definition.breakdown]);
      } else {
        setBreakdowns([]);
      }
      // Hydrate breakdown display options (same as insights)
      if (typeof q.definition?.breakdownLimit === 'number' && q.definition.breakdownLimit >= 1) {
        setBreakdownLimit(q.definition.breakdownLimit);
        setBreakdownCustomLimitInput(String(q.definition.breakdownLimit));
      }
      if (q.definition?.breakdownSortOrder && ['segment-a-z', 'segment-z-a', 'average-asc', 'average-desc'].includes(q.definition.breakdownSortOrder)) {
        setBreakdownSortOrder(q.definition.breakdownSortOrder);
      }
      if (q.definition?.breakdownShowMax === true) {
        setBreakdownShowMax(true);
      }
      const t = q.time;
      if (t && t.start && t.end) setInsightTimeFilter({ type: "custom", from: t.start, to: t.end });
      else if (t && t.preset) setGlobalTimeRange(t.preset);
      if (t?.interval && INSIGHT_SERIES_INTERVALS.includes(t.interval)) {
        setInsightSeriesInterval(t.interval);
      }

      // Hydrate measuring-related fields
      if (q.definition?.measuring) {
        setFunnelMeasuring(q.definition.measuring);
      }
      if (q.definition?.measuringProperty) {
        setFunnelMeasuringProperty(q.definition.measuringProperty);
      }
      if (q.definition?.measuringAggregation) {
        setFunnelMeasuringAggregation(q.definition.measuringAggregation);
      }
      if (q.definition?.measuringPercentileValue != null) {
        setFunnelMeasuringPercentileValue(q.definition.measuringPercentileValue);
      }
      if (q.definition?.conversionWindow) {
        setFunnelConversionWindow(q.definition.conversionWindow);
      }
      
      return;
    }

    if (editorType === "retention" && q.definition) {
      const def = q.definition;
      if (def.startEvent) setRetentionStartEvent(def.startEvent);
      if (def.returnEvent) setRetentionReturnEvent(def.returnEvent);
      if (def.retentionRule) setRetentionRule(def.retentionRule);
      if (def.metric) setRetentionMetric(def.metric);
      if (def.countSubject === "group") {
        setRetentionCountSubject("group");
        setRetentionAccumulationGroupKey(def.accumulationGroupKey ?? null);
      } else {
        setRetentionCountSubject("user");
        setRetentionAccumulationGroupKey(null);
      }
      setCardTitle(editingCard.title ?? "");
      const vis = editingCard.visualization || q.visualization || {};
      setChartType(vis.chartType || "retention-curve");
      if (vis.config && typeof vis.config === "object") setChartConfig(vis.config);
      const defFilters = def?.filters;
      if (defFilters && Array.isArray(defFilters) && defFilters.length > 0) {
        setGlobalFilters(defFilters.map((f, i) => ({
          id: f.id || `global-filter-${i}-${Date.now()}`,
          field: f.field ?? f.property,
          property: f.field ?? f.property,
          operator: f.operator,
          value: f.value,
          value2: f.value2,
          propertyType: f.propertyType,
          propertySource: f.propertySource,
          category: f.category || "global",
          listMode: f.listMode,
          listItemType: f.listItemType,
          propertyLabel: f.propertyLabel,
          connector: i === 0 ? "WHERE" : "AND",
        })));
      } else {
        setGlobalFilters([]);
      }
      // Load breakdown (retention uses single breakdown, not array)
      if (def.breakdown) {
        setBreakdowns([def.breakdown]);
      } else {
        setBreakdowns([]);
      }
      const t = q.time;
      if (t?.interval && ["day", "week", "month"].includes(t.interval)) {
        setRetentionInterval(t.interval);
      }
      if (t && t.start && t.end) setInsightTimeFilter({ type: "custom", from: t.start, to: t.end });
      else if (t && t.preset) setGlobalTimeRange(t.preset);
      return;
    }

    if (editorType !== "insights") return;
    const defMetrics = q.definition?.metrics;
    if (defMetrics && Array.isArray(defMetrics) && defMetrics.length > 0) {
      setMetrics(
        defMetrics.map((m) => ({
          id: m.id || `m${Math.random().toString(36).slice(2, 9)}`,
          aggregation: m.aggregation || "count",
          axis: m.axis || "left",
          metricName: (m.metricName || m.metric_name || "").trim() || undefined,
          events: (m.events || []).map((ev) => ({
            name: ev.name || "",
            source: ev.source || "event",
            filters: Array.isArray(ev.filters) ? ev.filters : [],
          })),
        }))
      );
    }
    setCardTitle(editingCard.title ?? "");
    // Hydrate global filters from saved query so they apply when report runs
    const defFilters = q.definition?.filters;
    if (defFilters && Array.isArray(defFilters) && defFilters.length > 0) {
      setGlobalFilters(defFilters.map((f, i) => ({
        id: f.id || `global-filter-${i}-${Date.now()}`,
        field: f.field ?? f.property,
        property: f.field ?? f.property,
        operator: f.operator,
        value: f.value,
        value2: f.value2,
        propertyType: f.propertyType,
        propertySource: f.propertySource,
        category: f.category || 'global',
        listMode: f.listMode,
        listItemType: f.listItemType,
        propertyLabel: f.propertyLabel,
        connector: i === 0 ? 'WHERE' : 'AND',
      })));
    } else {
      setGlobalFilters([]);
    }
    // Load breakdowns (support both new array format and legacy single breakdown)
    const defBreakdowns = q.definition?.breakdowns;
    if (defBreakdowns && Array.isArray(defBreakdowns) && defBreakdowns.length > 0) {
      setBreakdowns(defBreakdowns);
    } else if (q.definition?.breakdown) {
      // Legacy: convert single breakdown to array
      setBreakdowns([q.definition.breakdown]);
    } else {
      setBreakdowns([]);
    }
    // Hydrate breakdown display options (top N, sort order) so editor and board match
    if (typeof q.definition?.breakdownLimit === 'number' && q.definition.breakdownLimit >= 1) {
      setBreakdownLimit(q.definition.breakdownLimit);
      setBreakdownCustomLimitInput(String(q.definition.breakdownLimit));
    }
    if (q.definition?.breakdownSortOrder && ['segment-a-z', 'segment-z-a', 'average-asc', 'average-desc'].includes(q.definition.breakdownSortOrder)) {
      setBreakdownSortOrder(q.definition.breakdownSortOrder);
    }
    if (q.definition?.breakdownShowMax === true) {
      setBreakdownShowMax(true);
    }
    // Prefer top-level card.visualization (where we persist chart customization) over queryDsl.visualization
    const vis = editingCard.visualization || q.visualization || {};
    const ct = vis.chartType || "line";
    setChartType(ct);
    if (vis.config && typeof vis.config === "object") {
      setChartConfig(vis.config);
    }
    // Hydrate time filter from saved queryDsl so custom date range is restored
    const t = q.time;
    if (t && t.start && t.end) {
      setInsightTimeFilter({ type: "custom", from: t.start, to: t.end });
    } else if (t && t.preset) {
      setInsightTimeFilter(null);
      setGlobalTimeRange(t.preset);
    } else {
      setInsightTimeFilter(null);
    }
    if (t?.interval && INSIGHT_SERIES_INTERVALS.includes(t.interval)) {
      setInsightSeriesInterval(t.interval);
    } else {
      setInsightSeriesInterval("day");
    }
  }, [editorType, cardIdParam, editingCard, setGlobalTimeRange]);

  // If no teamId from localStorage, use team name from URL (backend can look it up)
  const teamIdOrName = teamId || team;

  const buildFunnelQueryDsl = useCallback(() => {
    if (reportType !== "funnel") return null;
    const timeRange = getInsightTimeRange(
      insightTimeFilter,
      globalTimeRange,
      reportType === "insight" || reportType === "funnel" ? insightSeriesInterval : "day"
    );
    const baseState = {
      timeRange,
      chartType,
      globalFilters,
      breakdowns,
      breakdownLimit,
      breakdownSortOrder,
      breakdownShowMax,
    };
    const validSteps = funnelSteps.filter((s) => (s.eventName || "").trim());
    if (validSteps.length === 0) return null;
    return analyticsApi.buildReportQuery("funnel", {
      ...baseState,
      funnelSteps: validSteps,
      measuring: funnelMeasuring,
      measuringProperty: funnelMeasuringProperty,
      measuringAggregation: funnelMeasuringAggregation,
      measuringPercentileValue: funnelMeasuringPercentileValue,
      conversionWindow: funnelConversionWindow,
      anyOrder: funnelAnyOrder,
      countSubject: funnelCountSubject,
      accumulationGroupKey: funnelAccumulationGroupKey,
    });
  }, [
    reportType,
    insightTimeFilter,
    globalTimeRange,
    insightSeriesInterval,
    chartType,
    globalFilters,
    breakdowns,
    breakdownLimit,
    breakdownSortOrder,
    breakdownShowMax,
    funnelSteps,
    funnelMeasuring,
    funnelMeasuringProperty,
    funnelMeasuringAggregation,
    funnelMeasuringPercentileValue,
    funnelConversionWindow,
    funnelAnyOrder,
    funnelCountSubject,
    funnelAccumulationGroupKey,
  ]);

  const buildFunnelStepUsersQueryDsl = useCallback(() => {
    const base = buildFunnelQueryDsl();
    if (!base) return null;
    const slice = funnelStepUsersContext.timeSlice;
    if (!slice?.start || !slice?.end) return base;
    return {
      ...base,
      time: {
        ...base.time,
        preset: null,
        start: slice.start,
        end: slice.end,
      },
    };
  }, [buildFunnelQueryDsl, funnelStepUsersContext.timeSlice]);

  const buildInsightEditorQueryDsl = useCallback(() => {
    if (reportType !== "insight") return null;
    const timeRange = getInsightTimeRange(
      insightTimeFilter,
      globalTimeRange,
      insightSeriesInterval
    );
    const baseState = {
      timeRange,
      chartType,
      globalFilters,
      breakdowns,
      breakdownLimit,
      breakdownSortOrder,
      breakdownShowMax,
    };
    if (metrics.length > 0) {
      const hasEvents = metrics.some((m) => m.events && m.events.length > 0);
      if (!hasEvents) return null;
      const metricsWithEvents = metrics
        .filter((m) => m.events && m.events.length > 0)
        .map((m) => ({
          id: m.id,
          aggregation: m.aggregation || "count",
          axis: m.axis || "left",
          metricName: m.metricName,
          events: (m.events || []).map((ev) => ({
            name: ev.name,
            eventType: ev.source || ev.eventType || "event",
            entityKind: ev.entityKind || 'event',
            ...(ev.spanKind ? { spanKind: ev.spanKind } : {}),
            filters: (ev.filters || []).map((f) => ({
              property: f.field ?? f.property,
              operator: f.operator,
              value: f.value,
              value2: f.value2,
              propertyType: f.propertyType,
              propertySource: f.propertySource,
              category: f.category,
              listMode: f.listMode,
              listItemType: f.listItemType,
            })),
          })),
        }));
      if (metricsWithEvents.length === 0) return null;
      return analyticsApi.buildReportQuery("insight", { ...baseState, metrics: metricsWithEvents });
    }
    if (selectedEvent) {
      const filters = (metricFilters || []).map((f) => ({
        property: f.property ?? f.field,
        operator: f.operator,
        value: f.value,
        category: f.category || "event_properties",
        eventName: f.eventName || selectedEvent,
      }));
      return analyticsApi.buildReportQuery("insight", {
        ...baseState,
        event: selectedEvent,
        aggregation: selectedAggregation,
        filters,
      });
    }
    return null;
  }, [
    reportType,
    insightTimeFilter,
    globalTimeRange,
    insightSeriesInterval,
    chartType,
    globalFilters,
    breakdowns,
    breakdownLimit,
    breakdownSortOrder,
    breakdownShowMax,
    metrics,
    selectedEvent,
    selectedAggregation,
    metricFilters,
  ]);

  const buildInsightDatapointUsersQueryDsl = useCallback(() => {
    const base = buildInsightEditorQueryDsl();
    if (!base) return null;
    const slice = insightDatapointContext.timeSlice;
    if (!slice?.start || !slice?.end) return base;
    return {
      ...base,
      time: {
        ...base.time,
        preset: null,
        start: slice.start,
        end: slice.end,
      },
    };
  }, [buildInsightEditorQueryDsl, insightDatapointContext.timeSlice]);

  const handleRequestInsightDatapointUsers = useCallback(
    ({ metricId, breakdownValues, timeSlice, periodLabel, metricTitle }) => {
      setInsightDatapointContext({
        metricId,
        breakdownValues: breakdownValues ?? null,
        stepTitle: metricTitle || "Metric",
        timeSlice: timeSlice ?? null,
        periodLabel: periodLabel ?? null,
      });
      setInsightDatapointUsersOpen(true);
    },
    []
  );

  const handleRequestFunnelStepUsers = useCallback(
    ({ stepIndex, breakdownValues, timeSlice, periodLabel, stepTitle: titleOverride }) => {
      const stepsData = result?.data?.steps ?? result?.steps;
      const st = Array.isArray(stepsData) ? stepsData[stepIndex] : null;
      const title = titleOverride || st?.label || st?.eventName || `Step ${stepIndex + 1}`;
      setFunnelStepUsersContext({
        stepIndex,
        breakdownValues: breakdownValues ?? null,
        stepTitle: title,
        timeSlice: timeSlice ?? null,
        periodLabel: periodLabel ?? null,
      });
      setFunnelStepUsersOpen(true);
    },
    [result]
  );

  const handleRequestFunnelLineBucketUsers = useCallback(
    ({ rowDate, segmentLabel }) => {
      const stepsData = result?.data?.steps ?? result?.steps;
      if (!Array.isArray(stepsData) || stepsData.length < 2) return;
      const lastIdx = stepsData.length - 1;
      const range = funnelChartBucketRange(rowDate, insightSeriesInterval);
      if (!range) return;
      let bv = null;
      if (segmentLabel != null && breakdowns?.length) {
        bv = segmentLabelToBreakdownValues(segmentLabel, breakdowns.length);
      }
      const st = stepsData[lastIdx];
      const stepName = st?.label || st?.eventName || `Step ${lastIdx + 1}`;
      const pl = formatFunnelBucketPeriodLabel(rowDate, insightSeriesInterval);
      setFunnelStepUsersContext({
        stepIndex: lastIdx,
        breakdownValues: bv,
        stepTitle: stepName,
        timeSlice: range,
        periodLabel: pl,
      });
      setFunnelStepUsersOpen(true);
    },
    [result, insightSeriesInterval, breakdowns]
  );
  
  // Retention events can be a plain string (legacy / event entityKind) or
  // an object `{ entityName, entityKind, spanKind }` for agent/span. Coerce
  // to a string before calling .trim().
  const retentionEventName = (v) =>
    typeof v === 'object' && v ? String(v.entityName || v.eventName || '') : String(v || '');
  const hasMetric =
    reportType === "funnel"
      ? funnelSteps.length > 0 && funnelSteps.some((s) => (s.eventName || "").trim())
      : reportType === "retention"
      ? retentionEventName(retentionStartEvent).trim() && retentionEventName(retentionReturnEvent).trim()
      : (metrics.length > 0 || !!selectedEvent);

  const insightDatapointDrillEnabled =
    reportType === "insight" && INSIGHT_DATAPOINT_DRILL_CHART_IDS.has(chartType) && hasMetric;

  // Load events on mount
  useEffect(() => {
    if (loadEvents && teamIdOrName) {
      loadEvents(teamIdOrName).catch(err => {
        console.error('InsightsEditorPage: Error in loadEvents:', err);
      });
    }
  }, [loadEvents, teamIdOrName, team]);

  // Debounce execute to avoid 2–3 rapid runs when state settles (e.g. open editor, hydrate card, filter change)
  const executeDebounceMs = 400;
  const executeTimeoutRef = useRef(null);

  // Execute query when metrics or funnel steps change (single path: buildReportQuery + execute), debounced
  useEffect(() => {
    const timeRange = getInsightTimeRange(
      insightTimeFilter,
      globalTimeRange,
      reportType === "insight" || reportType === "funnel" ? insightSeriesInterval : "day"
    );
    const baseState = { timeRange, chartType, globalFilters, breakdowns, breakdownLimit, breakdownSortOrder, breakdownShowMax };

    let queryDSL = null;
    if (reportType === "funnel") {
      const validSteps = funnelSteps.filter((s) => (s.eventName || "").trim());
      if (validSteps.length === 0) return;
      queryDSL = analyticsApi.buildReportQuery("funnel", {
        ...baseState,
        funnelSteps: validSteps,
        measuring: funnelMeasuring,
        measuringProperty: funnelMeasuringProperty,
        measuringAggregation: funnelMeasuringAggregation,
        measuringPercentileValue: funnelMeasuringPercentileValue,
        conversionWindow: funnelConversionWindow,
        anyOrder: funnelAnyOrder,
        countSubject: funnelCountSubject,
        accumulationGroupKey: funnelAccumulationGroupKey,
      });
    } else if (reportType === "retention") {
      // Retention events may be plain strings (legacy / event entityKind)
      // or objects { entityName, entityKind, spanKind } for agent/span.
      const startName = typeof retentionStartEvent === 'object' && retentionStartEvent
        ? (retentionStartEvent.entityName || '')
        : String(retentionStartEvent || '');
      const returnName = typeof retentionReturnEvent === 'object' && retentionReturnEvent
        ? (retentionReturnEvent.entityName || '')
        : String(retentionReturnEvent || '');
      if (!startName.trim() || !returnName.trim()) return;
      const retentionTimeRange = { ...timeRange, interval: retentionInterval || "day" };
      const startPayload = (typeof retentionStartEvent === 'object' && retentionStartEvent && retentionStartEvent.entityKind && retentionStartEvent.entityKind !== 'event')
        ? retentionStartEvent
        : startName.trim();
      const returnPayload = (typeof retentionReturnEvent === 'object' && retentionReturnEvent && retentionReturnEvent.entityKind && retentionReturnEvent.entityKind !== 'event')
        ? retentionReturnEvent
        : returnName.trim();
      queryDSL = analyticsApi.buildReportQuery("retention", {
        ...baseState,
        timeRange: retentionTimeRange,
        startEvent: startPayload,
        returnEvent: returnPayload,
        startEventSource: retentionStartEventSource || "event",
        returnEventSource: retentionReturnEventSource || "event",
        retentionRule: retentionRule,
        metric: retentionMetric,
        breakdown: breakdowns && breakdowns.length > 0 ? breakdowns[0] : null,
        countSubject: retentionCountSubject,
        accumulationGroupKey: retentionAccumulationGroupKey,
      });
    } else if (metrics.length > 0) {
      const hasEvents = metrics.some((m) => m.events && m.events.length > 0);
      if (!hasEvents) return;
      const metricsWithEvents = metrics
        .filter((m) => m.events && m.events.length > 0)
        .map((m) => ({
          id: m.id,
          aggregation: m.aggregation || "count",
          axis: m.axis || "left",
          metricName: m.metricName,
          events: (m.events || []).map((ev) => ({
            name: ev.name,
            eventType: ev.source || ev.eventType || "event",
            entityKind: ev.entityKind || 'event',
            ...(ev.spanKind ? { spanKind: ev.spanKind } : {}),
            filters: (ev.filters || []).map((f) => ({
              property: f.field ?? f.property,
              operator: f.operator,
              value: f.value,
              value2: f.value2,
              propertyType: f.propertyType,
              propertySource: f.propertySource,
              category: f.category,
              listMode: f.listMode,
              listItemType: f.listItemType,
            })),
          })),
        }));
      if (metricsWithEvents.length === 0) return;
      queryDSL = analyticsApi.buildReportQuery("insight", { ...baseState, metrics: metricsWithEvents });
    } else if (selectedEvent) {
      const filters = (metricFilters || []).map((f) => ({
        property: f.property ?? f.field,
        operator: f.operator,
        value: f.value,
        category: f.category || "event_properties",
        eventName: f.eventName || selectedEvent,
      }));
      queryDSL = analyticsApi.buildReportQuery("insight", { ...baseState, event: selectedEvent, aggregation: selectedAggregation, filters });
    }

    if (!queryDSL) return;

    // Debounce: wait for state to settle so we only run once instead of 2–3 times
    if (executeTimeoutRef.current) clearTimeout(executeTimeoutRef.current);
    beginNewQuery();
    executeTimeoutRef.current = setTimeout(() => {
      executeTimeoutRef.current = null;
      execute(queryDSL, teamIdOrName);
    }, executeDebounceMs);

    return () => {
      if (executeTimeoutRef.current) {
        clearTimeout(executeTimeoutRef.current);
        executeTimeoutRef.current = null;
        resetLoading();
      }
    };
  }, [reportType, funnelSteps, funnelMeasuring, funnelMeasuringProperty, funnelMeasuringAggregation, funnelMeasuringPercentileValue, funnelConversionWindow, funnelAnyOrder, funnelCountSubject, funnelAccumulationGroupKey, retentionStartEvent, retentionReturnEvent, retentionInterval, retentionRule, retentionMetric, retentionCountSubject, retentionAccumulationGroupKey, metrics, selectedEvent, selectedAggregation, globalTimeRange, insightTimeFilter, insightSeriesInterval, chartType, metricFilters, globalFilters, breakdowns, breakdownLimit, breakdownSortOrder, breakdownShowMax, execute, beginNewQuery, resetLoading, teamIdOrName]);

  // Close breakdown limit dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (breakdownLimitDropdownRef.current && !breakdownLimitDropdownRef.current.contains(event.target)) {
        setIsBreakdownLimitDropdownOpen(false);
      }
    };

    if (isBreakdownLimitDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isBreakdownLimitDropdownOpen]);

  const executeCardSave = async (effectiveTitle) => {
    try {
      const timeRange = getInsightTimeRange(
        insightTimeFilter,
        globalTimeRange,
        reportType === "insight" || reportType === "funnel" ? insightSeriesInterval : "day"
      );
      const baseState = { timeRange, chartType, globalFilters, breakdowns, breakdownLimit: breakdownShowMax ? 50 : breakdownLimit, breakdownSortOrder, breakdownShowMax };

      let queryDSL = null;
      if (reportType === "funnel") {
        queryDSL = analyticsApi.buildReportQuery("funnel", {
          ...baseState,
          funnelSteps: funnelSteps.filter((s) => (s.eventName || "").trim()),
          measuring: funnelMeasuring,
          measuringProperty: funnelMeasuringProperty,
          measuringAggregation: funnelMeasuringAggregation,
          measuringPercentileValue: funnelMeasuringPercentileValue,
          conversionWindow: funnelConversionWindow,
          anyOrder: funnelAnyOrder,
          countSubject: funnelCountSubject,
          accumulationGroupKey: funnelAccumulationGroupKey,
        });
      } else if (reportType === "retention") {
        const retentionTimeRange = { ...timeRange, interval: retentionInterval || "day" };
        // Object form for agent/span entity refs; bare string for events.
        const startName = typeof retentionStartEvent === 'object' && retentionStartEvent
          ? (retentionStartEvent.entityName || '')
          : String(retentionStartEvent || '');
        const returnName = typeof retentionReturnEvent === 'object' && retentionReturnEvent
          ? (retentionReturnEvent.entityName || '')
          : String(retentionReturnEvent || '');
        const startPayload = (typeof retentionStartEvent === 'object' && retentionStartEvent && retentionStartEvent.entityKind && retentionStartEvent.entityKind !== 'event')
          ? retentionStartEvent
          : startName.trim();
        const returnPayload = (typeof retentionReturnEvent === 'object' && retentionReturnEvent && retentionReturnEvent.entityKind && retentionReturnEvent.entityKind !== 'event')
          ? retentionReturnEvent
          : returnName.trim();
        queryDSL = analyticsApi.buildReportQuery("retention", {
          ...baseState,
          timeRange: retentionTimeRange,
          startEvent: startPayload,
          returnEvent: returnPayload,
          startEventSource: retentionStartEventSource || "event",
          returnEventSource: retentionReturnEventSource || "event",
          retentionRule: retentionRule,
          metric: retentionMetric,
          breakdown: breakdowns && breakdowns.length > 0 ? breakdowns[0] : null,
          countSubject: retentionCountSubject,
          accumulationGroupKey: retentionAccumulationGroupKey,
        });
      } else if (metrics.length > 0) {
        const metricsForDsl = metrics.map((m) => ({
          id: m.id,
          aggregation: m.aggregation || "count",
          axis: m.axis || "left",
          metricName: m.metricName,
          events: (m.events || []).map((ev) => ({
            name: ev.name,
            eventType: ev.source || ev.eventType || "event",
            entityKind: ev.entityKind || 'event',
            ...(ev.spanKind ? { spanKind: ev.spanKind } : {}),
            filters: (ev.filters || []).map((f) => ({
              property: f.field ?? f.property,
              operator: f.operator,
              value: f.value,
              value2: f.value2,
              propertyType: f.propertyType,
              propertySource: f.propertySource,
              category: f.category,
              listMode: f.listMode,
              listItemType: f.listItemType,
            })),
          })),
        }));
        queryDSL = analyticsApi.buildReportQuery("insight", { ...baseState, metrics: metricsForDsl });
      } else {
        const filters = (metricFilters || []).map((f) => ({
          property: f.property ?? f.field,
          operator: f.operator,
          value: f.value,
          category: f.category || "event_properties",
          eventName: f.eventName || selectedEvent,
        }));
        queryDSL = analyticsApi.buildReportQuery("insight", { ...baseState, event: selectedEvent, aggregation: selectedAggregation, filters });
      }

      const cardTypeForApi = reportType === "funnel" ? "funnel" : reportType === "retention" ? "retention" : "insights";

      // Editing existing card on a board or home workspace: update in place
      if (cardIdParam && editingCard && (isHomeEditor || boardId)) {
        const updated = await updateCard(cardIdParam, {
          title: effectiveTitle,
          queryDsl: queryDSL,
          visualization: { chartType, config: chartConfig || {} },
        });
        if (isHomeEditor && teamId != null && updated) upsertCardInHomeWorkspaceCache(teamId, updated);
        navigate(isHomeEditor ? `/${team}/home` : `/${team}/analytics/boards/${boardId}`);
        return;
      }

      const position =
        gridX != null && gridY != null
          ? { x: gridX, y: gridY, w: gridW ?? 6, h: gridH_param ?? 4 }
          : undefined;
      const cardBody = {
        cardType: cardTypeForApi,
        title: effectiveTitle,
        queryDsl: queryDSL,
        visualization: {
          chartType,
          config: chartConfig || {}
        },
        ...(position && { position })
      };

      if (isHomeEditor) {
        const res = await analyticsApi.createHomeWorkspaceCard(cardBody, teamId);
        const newCard = res?.data;
        if (newCard && teamId != null) upsertCardInHomeWorkspaceCache(teamId, newCard);
        navigate(`/${team}/home`);
        return;
      }

      // New card while already on a concrete board (e.g. added from board): save there directly
      if (boardId && boardId !== "new") {
        await analyticsApi.createCard(boardId, cardBody, teamId);
        navigate(`/${team}/analytics/boards/${boardId}`);
        return;
      }

      // boards/new/editor (or missing board): ask which board to use
      pendingSaveRef.current = { cardBody };
      setSaveBoardModalOpen(true);
    } catch (err) {
      console.error("Failed to save card:", err);
      await appAlert("Failed to save card. Please try again.", { title: "Save failed", variant: "error" });
    }
  };

  const handleSave = async () => {
    if (reportType === "funnel") {
      if (funnelSteps.length === 0 || !funnelSteps.some((s) => (s.eventName || "").trim())) {
        await appAlert("Please add at least one funnel step.", { title: "Cannot save" });
        return;
      }
    } else if (reportType === "retention") {
      if (!retentionEventName(retentionStartEvent).trim() || !retentionEventName(retentionReturnEvent).trim()) {
        await appAlert("Please select both start event and return event.", { title: "Cannot save" });
        return;
      }
    } else if (metrics.length === 0 && !selectedEvent) {
      await appAlert("Please add at least one metric.", { title: "Cannot save" });
      return;
    }

    const effectiveTitle = (cardTitle || "").trim();
    if (isBlankOrDefaultChartTitle(effectiveTitle)) {
      setChartNameModalOpen(true);
      return;
    }

    setEditorSaveLoading(true);
    try {
      await executeCardSave(effectiveTitle);
    } finally {
      setEditorSaveLoading(false);
    }
  };

  const handleChartNameModalConfirm = (name) => {
    setCardTitle(name);
    setChartNameModalOpen(false);
    setEditorSaveLoading(true);
    void executeCardSave(name.trim()).finally(() => setEditorSaveLoading(false));
  };

  const finishSaveToExistingBoard = async (targetBoardId) => {
    const p = pendingSaveRef.current;
    if (!p?.cardBody) return;
    setSaveActionLoading(true);
    try {
      await analyticsApi.createCard(targetBoardId, p.cardBody, teamId);
      trackClientEvent(
        'card_saved_to_existing_board',
        {
          target_board_id: String(targetBoardId),
          insight_type: p.cardBody?.kind ?? p.cardBody?.type ?? null,
        },
        { category: 'product' }
      );
      pendingSaveRef.current = null;
      setSaveBoardModalOpen(false);
      navigate(`/${team}/analytics/boards/${targetBoardId}`);
    } catch (err) {
      console.error("Failed to save card:", err);
      await appAlert("Failed to save card. Please try again.", { title: "Save failed", variant: "error" });
    } finally {
      setSaveActionLoading(false);
    }
  };

  const finishSaveToNewBoard = async () => {
    const p = pendingSaveRef.current;
    if (!p?.cardBody) return;
    setSaveActionLoading(true);
    try {
      const title = (p.cardBody.title || "").trim() || "New board";
      const newBoard = await createBoard({
        name: title,
        description: "",
        time_range: { preset: globalTimeRange, interval: "day", timezone: "UTC" },
        filters: null,
        compare_mode: null,
        layout: null
      }, teamId);
      const newId = String(newBoard.id);
      await analyticsApi.createCard(newId, p.cardBody, teamId);
      trackClientEvent(
        'card_saved_to_new_board',
        {
          new_board_id: newId,
          insight_type: p.cardBody?.kind ?? p.cardBody?.type ?? null,
        },
        { category: 'product' }
      );
      pendingSaveRef.current = null;
      setSaveBoardModalOpen(false);
      navigate(`/${team}/analytics/boards/${newId}`);
    } catch (err) {
      console.error("Failed to save card:", err);
      await appAlert("Failed to save card. Please try again.", { title: "Save failed", variant: "error" });
    } finally {
      setSaveActionLoading(false);
    }
  };

  const handleClose = () => {
    if (isHomeEditor) {
      navigate(`/${team}/home`);
      return;
    }
    navigate(`/${team}/analytics/boards/${boardId || "new"}`);
  };

  const resolvedFunnelChartConfig = useMemo(
    () => ({
      ...DEFAULT_FUNNEL_CHART_CONFIG,
      ...(chartConfig && typeof chartConfig === "object" ? chartConfig : {}),
    }),
    [chartConfig]
  );

  const mergedFunnelTableColumns = useMemo(
    () => ({
      ...DEFAULT_FUNNEL_TABLE_COLUMNS,
      ...(chartConfig?.funnelTableColumns || {}),
    }),
    [chartConfig?.funnelTableColumns]
  );

  /** Resolved funnel chart type (TTC coerces funnel-steps → bar). */
  const funnelEffectiveChartType = useMemo(() => {
    if (reportType !== "funnel") return null;
    return (funnelMeasuring || "") === "time_to_convert" && chartType === "funnel-steps"
      ? "bar"
      : chartType;
  }, [reportType, funnelMeasuring, chartType]);

  /** Line chart + time-series table (same metric as chart Y-axis). */
  const funnelLineMeta = useMemo(() => {
    if (reportType !== "funnel" || !result?.data?.steps?.length) return null;
    const hasTs =
      result.data.timeSeriesBreakdowns?.length || result.data.timeSeries?.length;
    if (funnelEffectiveChartType !== "line" || !hasTs) return null;
    return buildFunnelLineData(result.data, funnelMeasuring || "conversion_rate");
  }, [reportType, result?.data, funnelEffectiveChartType, funnelMeasuring]);

  // Simple editor for Text or Media cards (when cardId is in URL)
  if ((editorType === "text" || editorType === "media") && cardIdParam && (boardId || isHomeEditor)) {
    return (
      <TextOrMediaCardEditor
        team={team}
        boardId={boardId}
        teamId={teamId}
        isHomeEditor={isHomeEditor}
        cardId={cardIdParam}
        editorType={editorType}
        card={editingCard}
        cards={cards}
        updateCard={updateCard}
        boardName={boardName}
      />
    );
  }

  return (
    <>
    <ReportLayout
      topBar={
        <AnalyticsTopBar
          chartName={cardTitle}
          onChartNameChange={(e) => setCardTitle(e.target.value)}
          showCreateNew={false}
          showTimeRange={false}
          showSaveClose={true}
          onSave={handleSave}
          onClose={handleClose}
          saveAnchorRef={saveAnchorRef}
          saveStatus={editorSaveLoading ? "saving" : undefined}
        />
      }
      reportType={reportType}
      headerProps={{
        selectedPreset:
          insightTimeFilter
            ? insightTimeFilter.type === "custom"
              ? "custom"
              : (insightTimeFilter.preset || "30d")
            : analyticsPresetToEventsPreset(globalTimeRange),
        onTimeFilterChange: (filter) => {
          setInsightTimeFilter(filter);
          if (filter.type === "preset" && filter.preset) {
            const analyticsPreset =
              filter.preset === "today" ? "Today" : filter.preset === "yesterday" ? "Yesterday" : filter.preset === "7d" ? "7D" : filter.preset === "30d" ? "30D" : filter.preset === "3m" ? "3M" : filter.preset === "6m" ? "6M" : filter.preset === "12m" ? "12M" : globalTimeRange;
            setGlobalTimeRange(analyticsPreset);
          }
        },
        chartType,
        onChartTypeChange: setChartType,
        insightSeriesInterval,
        onInsightSeriesIntervalChange: setInsightSeriesInterval,
        showInsightSeriesInterval:
          (reportType === "insight" && isInsightTimeSeriesChart(chartType)) ||
          (reportType === "funnel" && chartType === "line"),
        funnelMeasuring: reportType === "funnel" ? funnelMeasuring : undefined,
        funnelTableColumns: mergedFunnelTableColumns,
        onFunnelTableColumnsChange: (next) => {
          setChartConfig((c) => ({
            ...(c || {}),
            funnelTableColumns: next,
          }));
        },
        showFunnelTableSettings:
          reportType === "funnel" &&
          funnelEffectiveChartType !== "bar" &&
          funnelEffectiveChartType !== "metric" &&
          !funnelLineMeta &&
          chartType !== "table",
      }}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      rightPanel={
        <QueryBuilderPanel
          key={cardIdParam || "new"}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          reportType={reportType}
          funnelSteps={funnelSteps}
          onFunnelStepsChange={setFunnelSteps}
          funnelMeasuring={funnelMeasuring}
          onFunnelMeasuringChange={handleFunnelMeasuringChange}
          funnelMeasuringProperty={funnelMeasuringProperty}
          onFunnelMeasuringPropertyChange={setFunnelMeasuringProperty}
          funnelMeasuringAggregation={funnelMeasuringAggregation}
          onFunnelMeasuringAggregationChange={setFunnelMeasuringAggregation}
          funnelMeasuringPercentileValue={funnelMeasuringPercentileValue}
          onFunnelMeasuringPercentileValueChange={setFunnelMeasuringPercentileValue}
          funnelConversionWindow={funnelConversionWindow}
          onFunnelConversionWindowChange={setFunnelConversionWindow}
          funnelAnyOrder={funnelAnyOrder}
          onFunnelAnyOrderChange={setFunnelAnyOrder}
          funnelCountSubject={funnelCountSubject}
          onFunnelCountSubjectChange={setFunnelCountSubject}
          funnelAccumulationGroupKey={funnelAccumulationGroupKey}
          onFunnelAccumulationGroupKeyChange={setFunnelAccumulationGroupKey}
          retentionStartEvent={retentionStartEvent}
          onRetentionStartEventChange={setRetentionStartEvent}
          retentionReturnEvent={retentionReturnEvent}
          onRetentionReturnEventChange={setRetentionReturnEvent}
          retentionInterval={retentionInterval}
          onRetentionIntervalChange={setRetentionInterval}
          retentionRule={retentionRule}
          onRetentionRuleChange={setRetentionRule}
          retentionMetric={retentionMetric}
          onRetentionMetricChange={setRetentionMetric}
          retentionCountSubject={retentionCountSubject}
          onRetentionCountSubjectChange={setRetentionCountSubject}
          retentionAccumulationGroupKey={retentionAccumulationGroupKey}
          onRetentionAccumulationGroupKeyChange={setRetentionAccumulationGroupKey}
          metrics={metrics}
          onMetricsChange={setMetrics}
          availableEvents={events}
          chartType={chartType}
          onChartTypeChange={setChartType}
          teamId={teamIdOrName}
          globalFilters={globalFilters}
          onGlobalFiltersChange={setGlobalFilters}
          breakdowns={breakdowns}
          onBreakdownsChange={setBreakdowns}
          visualizationConfig={chartConfig}
          onVisualizationConfigChange={setChartConfig}
          retentionIntervals={result?.data?.intervals ?? result?.intervals ?? []}
          selectedEvent={selectedEvent}
          onEventSelect={setSelectedEvent}
          selectedAggregation={selectedAggregation}
          onAggregationSelect={setSelectedAggregation}
          metricFilters={metricFilters}
          onMetricFiltersChange={setMetricFilters}
        />
      }
    >
            {!hasMetric ? (
              <InsightsEmptyState />
            ) : loading ? (
              <div className="p-6 flex-1 flex items-center justify-center">
                <div className="text-[13px] text-[color:var(--text-muted)]">
                  Loading data...
                </div>
              </div>
            ) : error ? (
              <div className="p-6 flex-1 flex items-center justify-center">
                <div className="text-sm" style={{ color: "var(--feedback-error)" }}>
                  Error: {error}
                </div>
              </div>
            ) : reportType === "retention" && (result?.data || result?.intervals) ? (
              /* Retention result - render based on chartType */
              <div className="min-w-0 p-6">
                {chartType === "metric" ? (
                  /* Metric view - show single retention rate */
                  <div className="w-full">
                    {(() => {
                      const avg = result?.data?.weightedAverage ?? result?.weightedAverage;
                      const intervals = result?.data?.intervals ?? result?.intervals ?? [];
                      const idx = chartConfig?.retentionLineIntervalIndex ?? 0;
                      const safeIdx =
                        Array.isArray(intervals) && intervals.length > 0
                          ? Math.min(Math.max(0, idx), intervals.length - 1)
                          : 0;
                      const groupLabel =
                        chartConfig?.retentionLineIntervalIndex != null && intervals?.[safeIdx]
                          ? intervals[safeIdx]
                          : "<1 Day";

                      let displayValue = retentionMetric === "retention_rate" ? "0%" : "0";
                      if (avg && Array.isArray(avg.values) && avg.values.length > 0) {
                        const selected = avg.values[safeIdx] ?? avg.values[0];
                        const rawNum = selected?.value ?? selected;
                        if (retentionMetric === "retention_rate") {
                          const num = Number(rawNum);
                          const pct = Number.isFinite(num) ? (num <= 1 ? num * 100 : num) : 0;
                          displayValue = `${pct.toFixed(2)}%`;
                        } else {
                          const users = Number(rawNum);
                          displayValue = Number.isFinite(users) ? formatNumber(users) : "0";
                        }
                      }

                      const baseColor =
                        (Array.isArray(chartConfig?.colors) && chartConfig.colors[0]) ||
                        "#7c3aed";

                      const hexToRgba = (hex, alpha) => {
                        const h = String(hex || "").replace("#", "").trim();
                        const full =
                          h.length === 3
                            ? h.split("").map((c) => c + c).join("")
                            : h.length === 6
                              ? h
                              : null;
                        if (!full) return `rgba(124, 58, 237, ${alpha})`;
                        const r = parseInt(full.slice(0, 2), 16);
                        const g = parseInt(full.slice(2, 4), 16);
                        const b = parseInt(full.slice(4, 6), 16);
                        if (![r, g, b].every((n) => Number.isFinite(n))) return `rgba(124, 58, 237, ${alpha})`;
                        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                      };

                      const hoverBg = hexToRgba(baseColor, 0.10);
                      const metricBreakdownDimensionLabel = String(
                        breakdowns?.[0]?.propertyLabel || breakdowns?.[0]?.property || ""
                      ).trim();
                      const segmentRows = result?.data?.breakdowns ?? result?.breakdowns ?? [];
                      const hasBreakdowns = Array.isArray(segmentRows) && segmentRows.length > 0;
                      const breakdownsForMetricCards = hasBreakdowns
                        ? sliceBreakdownsForChart(segmentRows, retentionMetricBreakdownTopN)
                        : [];

                      const breakdownPalette = [
                        "#7c3aed", "var(--feedback-info)", "#10b981", "var(--feedback-warning)",
                        "var(--feedback-error)", "#8b5cf6", "#06b6d4", "#84cc16",
                      ];

                      const formatMetricDisplay = (rawNum) => {
                        if (retentionMetric === "retention_rate") {
                          const num = Number(rawNum);
                          const pctNum = Number.isFinite(num) ? (num <= 1 ? num * 100 : num) : 0;
                          return `${pctNum.toFixed(2)}%`;
                        }
                        const users = Number(rawNum);
                        return Number.isFinite(users) ? formatNumber(users) : "0";
                      };

                      return (
                        <>
                          {hasBreakdowns ? (
                            <div className="w-full rounded-xl mb-6" className="border border-[color:var(--border-default)]" style={{ backgroundColor: "var(--surface-base)" }}>
                              <div className="p-4" className="border-b border-[color:var(--border-default)]">
                                <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                                  {`${retentionEventName(retentionStartEvent) || "Start Event"} then ${retentionEventName(retentionReturnEvent) || "Return Event"}`}
                                  {groupLabel ? ` • ${groupLabel}` : ""}
                                </div>
                              </div>

                              <div className="p-4 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                                {breakdownsForMetricCards.map((bd, i) => {
                                  const raw = Array.isArray(bd?.values) ? (bd.values[safeIdx] ?? bd.values[0]) : 0;
                                  const valueText = formatMetricDisplay(raw);
                                  const segLabel = bd?.segment || "(none)";
                                  const fullIdx = segmentRows.findIndex((b) => (b?.segment || "(none)") === segLabel);
                                  const paletteIdx = fullIdx >= 0 ? fullIdx : i;
                                  const color = breakdownPalette[paletteIdx % breakdownPalette.length] || baseColor;
                                  return (
                                    <div
                                      key={`${segLabel}-${paletteIdx}`}
                                      className="rounded-lg p-4"
                                      style={{
                                        border: "1px solid var(--border-default)",
                                        backgroundColor: "var(--surface-base)",
                                      }}
                                    >
                                      <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>
                                        <span
                                          style={{
                                            display: "inline-block",
                                            width: 8,
                                            height: 8,
                                            borderRadius: 2,
                                            backgroundColor: color,
                                            marginRight: 8,
                                            verticalAlign: "middle",
                                          }}
                                        />
                                        <span style={{ verticalAlign: "middle" }}>{segLabel}</span>
                                      </div>
                                      <div className="text-4xl font-bold" style={{ color: "var(--text-primary)" }}>
                                        {valueText}
                                      </div>
                                      <div className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                                        {retentionMetric === "retention_rate" ? "Retention Rate" : "Unique Users"}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            /* Metric panel layout */
                            <div
                              className="w-full flex items-center justify-center rounded-xl mb-6"
                              style={{
                                minHeight: "260px",
                                backgroundColor: "var(--surface-base)",
                                border: "1px solid var(--border-default)",
                                transition: "background-color 120ms ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = hoverBg;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "var(--surface-base)";
                              }}
                            >
                              <div className="text-center">
                                <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 8,
                                      height: 8,
                                      borderRadius: 2,
                                      backgroundColor: baseColor,
                                      marginRight: 8,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                  <span style={{ verticalAlign: "middle" }}>
                                    {`${retentionEventName(retentionStartEvent) || "Start Event"} then ${retentionEventName(retentionReturnEvent) || "Return Event"}`}
                                  </span>
                                </div>
                                <div className="text-6xl font-bold" style={{ color: "var(--brand-primary)" }}>
                                  {displayValue}
                                </div>
                                <div className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                                  {retentionMetric === "retention_rate" ? "Retention Rate" : "Unique Users"}
                                  {groupLabel ? ` • ${groupLabel}` : ""}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Table below metric */}
                          <RetentionMetricTable
                            label={`${retentionEventName(retentionStartEvent) || "Start Event"} then ${retentionEventName(retentionReturnEvent) || "Return Event"}`}
                            value={displayValue}
                            metricIsRetentionRate={retentionMetric === "retention_rate"}
                            breakdownDimensionLabel={metricBreakdownDimensionLabel}
                            breakdownChartTopN={hasBreakdowns ? retentionMetricBreakdownTopN : undefined}
                            onBreakdownChartTopNChange={
                              hasBreakdowns ? setRetentionMetricBreakdownTopN : undefined
                            }
                            rows={
                              hasBreakdowns
                                ? segmentRows.map((bd) => {
                                    const raw = Array.isArray(bd?.values) ? (bd.values[safeIdx] ?? bd.values[0]) : 0;
                                    const n = Number(raw);
                                    const sortValue =
                                      retentionMetric === "retention_rate"
                                        ? Number.isFinite(n)
                                          ? n <= 1 && n > 0
                                            ? n * 100
                                            : n
                                          : 0
                                        : Number.isFinite(n)
                                          ? n
                                          : 0;
                                    return {
                                      label: bd?.segment || "(none)",
                                      value: formatMetricDisplay(raw),
                                      sortValue,
                                    };
                                  })
                                : null
                            }
                          />
                        </>
                      );
                    })()}
                  </div>
                ) : chartType === "retention-curve" || chartType === "line" ? (
                  /* Retention curve vs calendar line chart + cohort table */
                  <RetentionVisualization
                    intervals={result?.data?.intervals ?? result?.intervals ?? []}
                    cohorts={result?.data?.cohorts ?? result?.cohorts ?? []}
                    weightedAverage={result?.data?.weightedAverage ?? result?.weightedAverage ?? null}
                    breakdowns={result?.data?.breakdowns ?? result?.breakdowns ?? []}
                    breakdownDimensionLabel={String(
                      breakdowns?.[0]?.propertyLabel || breakdowns?.[0]?.property || ""
                    ).trim()}
                    metric={retentionMetric}
                    config={chartConfig || {}}
                    chartType={chartType}
                    startEventLabel={retentionEventName(retentionStartEvent)}
                    returnEventLabel={retentionEventName(retentionReturnEvent)}
                  />
                ) : null}
              </div>
            ) : reportType === "funnel" && result?.data?.steps?.length > 0 ? (
              /* Funnel result - render either funnel steps view or line chart based on chartType */
              <div className="min-w-0 p-6">
                {/* Breakdown Controls - only show when breakdowns exist */}
                {result.data.breakdowns && result.data.breakdowns.length > 0 && (
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Sort Dropdown */}
                      <select
                        value={breakdownSortOrder}
                        onChange={(e) => setBreakdownSortOrder(e.target.value)}
                        className="px-3 py-1.5 text-sm border rounded-lg cursor-pointer outline-none"
                        style={{
                          backgroundColor: "var(--surface-base)",
                          borderColor: "var(--border-default)",
                          color: "var(--text-primary)"
                        }}
                      >
                        <option value="segment-a-z">Segment A → Z</option>
                        <option value="segment-z-a">Segment Z → A</option>
                        <option value="average-asc">Average 1 → 100</option>
                        <option value="average-desc">Average 100 → 1</option>
                      </select>

                      {/* Limit Dropdown */}
                      <div className="relative" ref={breakdownLimitDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsBreakdownLimitDropdownOpen(!isBreakdownLimitDropdownOpen)}
                          className="px-3 py-1.5 text-sm border rounded-lg flex items-center gap-2"
                          style={{
                            backgroundColor: "var(--surface-base)",
                            borderColor: "var(--border-default)",
                            color: "var(--text-primary)"
                          }}
                        >
                          Top {breakdownShowMax ? 50 : breakdownLimit}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {isBreakdownLimitDropdownOpen && (
                          <div
                            className="absolute top-full left-0 mt-1 p-4 rounded-lg border shadow-lg z-[100] min-w-[280px]"
                            style={{
                              backgroundColor: "var(--surface-base)",
                              borderColor: "var(--border-default)"
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="text-sm font-medium mb-2" style={{ color: "var(--text-primary)" }}>
                              Top Segments
                            </div>
                            <div className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
                              Choose the number of rows you want to see in the chart, up to 50.
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                              <input
                                type="number"
                                min="1"
                                max="50"
                                value={breakdownCustomLimitInput}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setBreakdownCustomLimitInput(val);
                                  const num = parseInt(val, 10);
                                  if (!isNaN(num) && num >= 1 && num <= 50) {
                                    setBreakdownLimit(num);
                                    setBreakdownShowMax(false);
                                  }
                                }}
                                onBlur={() => {
                                  const num = parseInt(breakdownCustomLimitInput, 10);
                                  if (isNaN(num) || num < 1) {
                                    setBreakdownCustomLimitInput(breakdownLimit.toString());
                                  } else if (num > 50) {
                                    setBreakdownCustomLimitInput('50');
                                    setBreakdownLimit(50);
                                    setBreakdownShowMax(false);
                                  } else {
                                    setBreakdownLimit(num);
                                    setBreakdownShowMax(false);
                                  }
                                }}
                                className="flex-1 px-2 py-1.5 text-sm border rounded"
                                style={{
                                  backgroundColor: "var(--surface-base)",
                                  borderColor: "var(--border-default)",
                                  color: "var(--text-primary)"
                                }}
                              />
                              <span className="text-sm" style={{ color: "var(--text-muted)" }}>segments</span>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={breakdownShowMax}
                                onChange={(e) => {
                                  setBreakdownShowMax(e.target.checked);
                                  if (e.target.checked) {
                                    setBreakdownLimit(50);
                                    setBreakdownCustomLimitInput('50');
                                  }
                                }}
                                className="w-4 h-4"
                              />
                              <span className="text-sm" style={{ color: "var(--text-primary)" }}>
                                Show max segments (up to 50)
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Funnel Chart / Metric Area */}
                <div className="mb-8">
                  {(() => {
                    const funnelChartType =
                      (funnelMeasuring || "") === "time_to_convert" &&
                      chartType === "funnel-steps"
                        ? "bar"
                        : chartType;

                    // Sort and limit breakdowns for all chart types
                    const sortedBreakdowns = sortAndLimitFunnelBreakdowns(
                      result.data.breakdowns || [],
                      breakdownLimit,
                      breakdownSortOrder,
                      breakdownShowMax
                    );

                    if (funnelChartType === "metric") {
                      return (
                        <div className="w-full px-1">
                          <FunnelMetricCards
                            steps={result.data.steps.map((step) => ({
                              ...step,
                              measuring:
                                step.measuring || funnelMeasuring || "conversion_rate",
                            }))}
                            breakdowns={sortedBreakdowns}
                            measuring={funnelMeasuring || "conversion_rate"}
                            seriesColors={resolvedFunnelChartConfig.colors}
                          />
                        </div>
                      );
                    } else if (funnelChartType === "line" && (result.data.timeSeriesBreakdowns?.length || result.data.timeSeries?.length)) {
                      const lineMeta =
                        funnelLineMeta ||
                        buildFunnelLineData(
                          result.data,
                          funnelMeasuring || "conversion_rate"
                        );
                      return (
                        <FunnelLineChart
                          points={
                            result.data.timeSeriesBreakdowns?.length
                              ? []
                              : lineMeta.points
                          }
                          title={lineMeta.title}
                                                    height={insightEditorChartHeightPx("line")}
                          breakdownSegments={
                            result.data.timeSeriesBreakdowns || null
                          }
                          measuring={funnelMeasuring || "conversion_rate"}
                          config={resolvedFunnelChartConfig}
                          timeGranularity={insightSeriesInterval}
                          enablePointDrilldown={reportType === "funnel"}
                          onRequestLineBucketUsers={handleRequestFunnelLineBucketUsers}
                        />
                      );
                    } else if (funnelChartType === "bar") {
                      if (result.data.breakdowns?.length) {
                        return (
                          <FunnelBreakdownBarChart
                            steps={result.data.steps.map((step) => ({
                              ...step,
                              measuring:
                                step.measuring ||
                                funnelMeasuring ||
                                "conversion_rate",
                            }))}
                            breakdowns={sortedBreakdowns}
                            measuring={funnelMeasuring || "conversion_rate"}
                            config={resolvedFunnelChartConfig}
                          />
                        );
                      } else {
                        return (
                          <FunnelSummaryBarChart
                            steps={result.data.steps.map(step => ({
                              ...step,
                              measuring:
                                step.measuring || funnelMeasuring || "conversion_rate",
                            }))}
                            measuring={funnelMeasuring || "conversion_rate"}
                            config={resolvedFunnelChartConfig}
                          />
                        );
                      }
                    } else {
                      return (
                        <FunnelVisualization
                          steps={result.data.steps.map(step => ({
                            ...step,
                            measuring:
                              step.measuring || funnelMeasuring || "conversion_rate",
                          }))}
                          measuring={funnelMeasuring || "conversion_rate"}
                          overallConversionRate={
                            result.data.steps.length > 1
                              ? result.data.steps[result.data.steps.length - 1]
                                  ?.conversion
                              : 100
                          }
                          breakdowns={sortedBreakdowns}
                          config={resolvedFunnelChartConfig}
                          enableStepUserDrilldown={reportType === "funnel"}
                          onRequestStepUsers={handleRequestFunnelStepUsers}
                        />
                      );
                    }
                  })()}
                </div>

                {/* Funnel table: time-series for line only; bar & metric have no table below */}
                {funnelLineMeta ? (
                  <div className="mt-8">
                    <FunnelTimeSeriesTable
                      points={funnelLineMeta.points}
                      breakdownSegments={
                        result.data.timeSeriesBreakdowns?.length
                          ? result.data.timeSeriesBreakdowns
                          : null
                      }
                      measuring={funnelMeasuring || "conversion_rate"}
                      metricTitle={funnelLineMeta.title}
                      seriesColors={resolvedFunnelChartConfig.colors}
                      timeGranularity={insightSeriesInterval}
                    />
                  </div>
                ) : funnelEffectiveChartType !== "bar" &&
                  funnelEffectiveChartType !== "metric" ? (
                  <div className="mt-8">
                    <FunnelDataTable
                      steps={result.data.steps.map((step) => ({
                        ...step,
                        measuring:
                          step.measuring || funnelMeasuring || "conversion_rate",
                      }))}
                      measuring={funnelMeasuring || "conversion_rate"}
                      breakdowns={sortAndLimitFunnelBreakdowns(
                        result.data.breakdowns || [],
                        breakdownLimit,
                        breakdownSortOrder,
                        breakdownShowMax
                      )}
                      tableColumns={mergedFunnelTableColumns}
                    />
                  </div>
                ) : null}
              </div>
            ) : result && result.series && result.series.length > 0 ? (
              <div className="min-w-0 flex flex-1 flex-col px-6 pt-4 lg:px-8">
                {(() => {
                  // Helper function to normalize, sort, and limit breakdowns
                  // When multiple metrics: sort and limit PER METRIC (each metric gets its own top N segments)
                  const normalizeSortAndLimitBreakdowns = (resultSeries, hasMultipleMetrics) => {
                    if (!resultSeries || resultSeries.length === 0) return [];
                    
                    // Check if this is breakdown format
                    const isBreakdownFormat = resultSeries[0].breakdowns && 
                                              Array.isArray(resultSeries[0].breakdowns) &&
                                              resultSeries[0].breakdowns.length > 0;
                    
                    if (!isBreakdownFormat) return resultSeries;
                    
                    const maxLimit = breakdownShowMax ? 50 : Math.min(breakdownLimit, 50);
                    const sortFn = (() => {
                      switch (breakdownSortOrder) {
                        case 'segment-a-z': return (a, b) => (a.originalBreakdownKey || '').localeCompare(b.originalBreakdownKey || '');
                        case 'segment-z-a': return (a, b) => (b.originalBreakdownKey || '').localeCompare(a.originalBreakdownKey || '');
                        case 'average-asc': return (a, b) => a.calculatedAverage - b.calculatedAverage;
                        case 'average-desc':
                        default: return (a, b) => b.calculatedAverage - a.calculatedAverage;
                      }
                    })();

                    const allSeries = [];
                    // Process each metric separately so breakdown segments are unique per metric
                    resultSeries.forEach((metricSeries, metricIndex) => {
                      if (!metricSeries.breakdowns || !Array.isArray(metricSeries.breakdowns)) return;
                      const metricIdentifier = String.fromCharCode(65 + metricIndex);
                      const metricSegments = [];
                      metricSeries.breakdowns.forEach((breakdown) => {
                        const breakdownValueLabel = breakdown.breakdownKey || 
                          (breakdown.breakdownValues && breakdown.breakdownValues.join(' / ')) ||
                          metricSeries.metricName;
                        // Header display: "Country" or "India/Gurugram" for hierarchical
                        const displayLabel = breakdown.breakdownValues && breakdown.breakdownValues.length > 0
                          ? breakdown.breakdownValues.join('/')
                          : (breakdown.breakdownKey || breakdownValueLabel);
                        const dataPoints = breakdown.data || [];
                        const sum = dataPoints.reduce((acc, point) => acc + (point.value || 0), 0);
                        const calculatedAverage = dataPoints.length > 0 ? sum / dataPoints.length : 0;
                        const segmentTotal = breakdown.total ?? sum;
                        metricSegments.push({
                          metricId: metricSeries.metricId,
                          metricName: displayLabel,
                          metricIdentifier,
                          displayLabel,
                          fullMetricName: metricSeries.metricName,
                          data: breakdown.data || [],
                          summary: {
                            ...(metricSeries.summary || {}),
                            total: segmentTotal,
                            average: calculatedAverage
                          },
                          breakdownKey: breakdown.breakdownKey,
                          breakdownValues: breakdown.breakdownValues,
                          breakdownDimensionNames: metricSeries.breakdownDimensionNames, // Preserve dimension names
                          calculatedAverage: calculatedAverage,
                          originalBreakdownKey: breakdownValueLabel
                        });
                      });
                      metricSegments.sort(sortFn);
                      allSeries.push(...metricSegments.slice(0, maxLimit));
                    });
                    return allSeries;
                  };

                  // Check if this is breakdown format
                  const isBreakdownFormat = result.series.length > 0 && 
                                            result.series[0].breakdowns && 
                                            Array.isArray(result.series[0].breakdowns) &&
                                            result.series[0].breakdowns.length > 0;
                  
                  const hasMultipleMetrics = result.series.length > 1;
                  const transformedSeries = isBreakdownFormat 
                    ? normalizeSortAndLimitBreakdowns(result.series, hasMultipleMetrics)
                    : result.series;

                  return (
                    <>
                      {/* Breakdown Controls - only show for breakdown format */}
                      {isBreakdownFormat && (
                        <div className="mb-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {/* Sort Dropdown */}
                            <select
                              value={breakdownSortOrder}
                              onChange={(e) => setBreakdownSortOrder(e.target.value)}
                              className="px-3 py-1.5 text-sm border rounded-lg cursor-pointer outline-none"
                              style={{
                                backgroundColor: "var(--surface-base)",
                                borderColor: "var(--border-default)",
                                color: "var(--text-primary)"
                              }}
                            >
                              <option value="segment-a-z">Segment A → Z</option>
                              <option value="segment-z-a">Segment Z → A</option>
                              <option value="average-asc">Average 1 → 100</option>
                              <option value="average-desc">Average 100 → 1</option>
                            </select>

                            {/* Limit Dropdown */}
                            <div className="relative" ref={breakdownLimitDropdownRef}>
                              <button
                                type="button"
                                onClick={() => setIsBreakdownLimitDropdownOpen(!isBreakdownLimitDropdownOpen)}
                                className="px-3 py-1.5 text-sm border rounded-lg flex items-center gap-2"
                                style={{
                                  backgroundColor: "var(--surface-base)",
                                  borderColor: "var(--border-default)",
                                  color: "var(--text-primary)"
                                }}
                              >
                                Top {breakdownShowMax ? 50 : breakdownLimit}
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>

                              {isBreakdownLimitDropdownOpen && (
                                <div
                                  className="absolute top-full left-0 mt-1 p-4 rounded-lg border shadow-lg z-[100] min-w-[280px]"
                                  style={{
                                    backgroundColor: "var(--surface-base)",
                                    borderColor: "var(--border-default)"
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="text-sm font-medium mb-2" style={{ color: "var(--text-primary)" }}>
                                    Top Segments
                                  </div>
                                  <div className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
                                    Choose the number of rows you want to see in the chart, up to 50.
                                  </div>
                                  <div className="flex items-center gap-2 mb-3">
                                    <input
                                      type="number"
                                      min="1"
                                      max="50"
                                      value={breakdownCustomLimitInput}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setBreakdownCustomLimitInput(val);
                                        const num = parseInt(val, 10);
                                        if (!isNaN(num) && num >= 1 && num <= 50) {
                                          setBreakdownLimit(num);
                                          setBreakdownShowMax(false);
                                        }
                                      }}
                                      onBlur={() => {
                                        const num = parseInt(breakdownCustomLimitInput, 10);
                                        if (isNaN(num) || num < 1) {
                                          setBreakdownCustomLimitInput(breakdownLimit.toString());
                                        } else if (num > 50) {
                                          setBreakdownCustomLimitInput('50');
                                          setBreakdownLimit(50);
                                          setBreakdownShowMax(false);
                                        } else {
                                          setBreakdownLimit(num);
                                          setBreakdownShowMax(false);
                                        }
                                      }}
                                      className="flex-1 px-2 py-1.5 text-sm border rounded"
                                      style={{
                                        backgroundColor: "var(--surface-base)",
                                        borderColor: "var(--border-default)",
                                        color: "var(--text-primary)"
                                      }}
                                    />
                                    <span className="text-sm" style={{ color: "var(--text-muted)" }}>segments</span>
                                  </div>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={breakdownShowMax}
                                      onChange={(e) => {
                                        setBreakdownShowMax(e.target.checked);
                                        if (e.target.checked) {
                                          setBreakdownLimit(50);
                                          setBreakdownCustomLimitInput('50');
                                        }
                                      }}
                                      className="w-4 h-4"
                                    />
                                    <span className="text-sm" style={{ color: "var(--text-primary)" }}>
                                      Show max segments (up to 50)
                                    </span>
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                {result.series.length === 1 ? (
                  <div className="mb-4 flex items-center gap-2">
                    <span className="text-sm" style={{ color: "var(--text-muted)" }}>
                      {result.series[0].metricName}
                    </span>
                    {result.series[0].summary && (
                      <span className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>
                        Total: {formatNumber(result.series[0].summary.total)}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    {result.series.map((s, idx) => (
                      <span key={idx} className="text-sm" style={{ color: "var(--text-muted)" }}>
                        {s.metricName}
                        {s.summary && ` (Total: ${formatNumber(s.summary.total)})`}
                        {idx < result.series.length - 1 && <span className="mx-2">•</span>}
                      </span>
                    ))}
                  </div>
                )}

                {/* Metric view: show metric cards (one per metric or one per breakdown segment when breakdown applied) */}
                {chartType === "metric" && (
                  <div className="rounded-xl border border-[color:var(--border-default)] bg-surface p-4 pb-6 shadow-sm">
                    <MetricCards
                      series={transformedSeries}
                      seriesColors={chartConfig?.colors}
                      hideTitleRow={transformedSeries.length === 1}
                    />
                  </div>
                )}

                {/* Chart or Table: when chartType === "table", render ONLY the metric table (no chart, no lower table) */}
                {chartType !== "metric" && (
                  <div
                    className={`rounded-xl border border-[color:var(--border-default)] bg-surface p-4 shadow-sm ${
                      chartType === "table" ? "min-h-[300px]" : "min-h-[380px]"
                    }`}
                  >
                    {chartType === "table" ? (
                      /* Table chart: single table only. With breakdown → GroupedFlatTable; without → InsightsChart Metric|Total */
                      isBreakdownFormat ? (
                        (() => {
                          const breakdownProperties = result.series[0]?.breakdownProperties;
                          const fullTotalsByMetric = result.series.reduce((acc, s) => {
                            const id = s.metricId ?? s.metricName;
                            if (id != null && s.summary?.total != null) acc[id] = s.summary.total;
                            return acc;
                          }, {});
                          return (
                            <InsightsDataTable
                              allSeries={transformedSeries}
                              seriesColors={chartConfig?.colors}
                              breakdownProperties={breakdownProperties}
                              breakdownFormat={true}
                              chartType="table"
                              fullTotalsByMetric={fullTotalsByMetric}
                            />
                          );
                        })()
                      ) : (
                        <InsightsChart
                          data={{
                            series: transformedSeries,
                            metrics,
                            breakdownProperties: isBreakdownFormat ? (result.series[0]?.breakdownProperties || null) : null,
                            summary: result.series.length === 1 ? {
                              avg: result.series[0].summary?.average ?? result.series[0].summary?.avg,
                              total: result.series[0].summary?.total,
                              delta: result.series[0].delta != null
                                ? `${result.series[0].delta.percentage >= 0 ? '+' : ''}${Number(result.series[0].delta.percentage).toFixed(1)}%`
                                : undefined,
                              metric: result.series[0].metricName,
                            } : undefined,
                          }}
                          chartType="table"
                          config={chartConfig}
                          height={insightEditorChartHeightPx("table")}
                        />
                      )
                    ) : (
                      /* Non-table: show chart */
                      <InsightsChart
                        data={{
                          series: transformedSeries,
                          metrics,
                          breakdownProperties: isBreakdownFormat ? (result.series[0]?.breakdownProperties || null) : null,
                          summary: result.series.length === 1 ? {
                            avg: result.series[0].summary?.average ?? result.series[0].summary?.avg,
                            total: result.series[0].summary?.total,
                            delta: result.series[0].delta != null
                              ? `${result.series[0].delta.percentage >= 0 ? '+' : ''}${Number(result.series[0].delta.percentage).toFixed(1)}%`
                              : undefined,
                            metric: result.series[0].metricName,
                          } : undefined,
                        }}
                        chartType={chartType}
                        config={chartConfig}
                        height={insightEditorChartHeightPx(chartType)}
                        timeGranularity={insightSeriesInterval}
                        enableDatapointDrilldown={insightDatapointDrillEnabled}
                        onRequestInsightDatapointUsers={handleRequestInsightDatapointUsers}
                      />
                    )}
                  </div>
                )}

                {/* Data table: only when chartType !== "table" (table chart shows single table above) */}
                {chartType !== "metric" && chartType !== "table" && (
                  <div className="pb-8">
                    {(() => {
                      if (isBreakdownFormat) {
                        const breakdownProperties = result.series[0]?.breakdownProperties;
                        return (
                          <InsightsDataTable
                            allSeries={transformedSeries}
                            seriesColors={chartConfig?.colors}
                            breakdownProperties={breakdownProperties}
                            breakdownFormat={true}
                          />
                        );
                      } else if (result.series.length === 1 && result.series[0].data) {
                        return (
                          <InsightsDataTable
                            series={result.series[0].data || []}
                            metricName={result.series[0].metricName}
                            average={result.series[0].summary?.average ?? result.series[0].summary?.avg}
                            seriesColors={chartConfig?.colors}
                          />
                        );
                      } else {
                        return (
                          <InsightsDataTable
                            allSeries={result.series}
                            seriesColors={chartConfig?.colors}
                          />
                        );
                      }
                    })()}
                  </div>
                )}
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="p-6 flex-1 flex items-center justify-center">
                <div className="text-sm" style={{ color: "var(--text-muted)" }}>
                  No data available
                </div>
              </div>
            )}
    </ReportLayout>
    <ChartNameModal
      open={chartNameModalOpen}
      onClose={() => setChartNameModalOpen(false)}
      initialValue={cardTitle}
      onConfirm={handleChartNameModalConfirm}
    />
    <SaveToBoardModal
      open={saveBoardModalOpen}
      anchorRef={saveAnchorRef}
      onClose={() => {
        if (!saveActionLoading) {
          setSaveBoardModalOpen(false);
          pendingSaveRef.current = null;
        }
      }}
      teamId={teamId}
      loadBoards={loadBoards}
      onSaveToExisting={finishSaveToExistingBoard}
      onSaveToNewBoard={finishSaveToNewBoard}
      saving={saveActionLoading}
    />
    <FunnelStepUsersPanel
      open={funnelStepUsersOpen}
      onClose={() => {
        setFunnelStepUsersOpen(false);
        setFunnelStepUsersContext({
          stepIndex: 0,
          breakdownValues: null,
          stepTitle: "",
          timeSlice: null,
          periodLabel: null,
        });
      }}
      teamSlug={team}
      teamIdOrName={teamIdOrName}
      stepTitle={funnelStepUsersContext.stepTitle}
      stepIndex={funnelStepUsersContext.stepIndex}
      breakdownValues={funnelStepUsersContext.breakdownValues}
      periodLabel={funnelStepUsersContext.periodLabel}
      buildQueryDsl={buildFunnelStepUsersQueryDsl}
    />
    <FunnelStepUsersPanel
      open={insightDatapointUsersOpen}
      onClose={() => {
        setInsightDatapointUsersOpen(false);
        setInsightDatapointContext({
          metricId: null,
          breakdownValues: null,
          stepTitle: "",
          timeSlice: null,
          periodLabel: null,
        });
      }}
      teamSlug={team}
      teamIdOrName={teamIdOrName}
      drillMode="insights"
      metricId={insightDatapointContext.metricId}
      stepTitle={insightDatapointContext.stepTitle}
      stepIndex={0}
      breakdownValues={insightDatapointContext.breakdownValues}
      periodLabel={insightDatapointContext.periodLabel}
      buildQueryDsl={buildInsightDatapointUsersQueryDsl}
    />
    </>
  );
}
